import moment from "moment-timezone";
import { createAction } from "redux-actions";
import { saveAs } from "file-saver";
import { apiGet, apiPost } from "util/api";
import { utcParse } from "d3-time-format";
import Immutable from "immutable";
import downloadPropsSelector from "./selectors";
import * as types from "./constants";
import { DATA_PROCESS_LEVELS } from "./constants";

const parseTime = utcParse("%Y-%m-%dT%H:%M:%S.%LZ");
const SEABIRD_MODELS = ["SBE19plus", "SBE25", "SBE25plus"];
// 11_qc_pi_research is a query-time view over 10_qc_pi data (bad data removed),
// not a processing_stage a cast is ever recorded as - so cast eligibility must
// still be checked against 10_qc_pi.
const getEligibilityStage = processingStageFilter =>
  processingStageFilter === "11_qc_pi_research"
    ? "10_qc_pi"
    : processingStageFilter;

/* eslint-disable max-len */
export const openPreviewFileDialog = createAction(
  types.PREVIEW_FILE_DIALOG_OPEN
);
export const closePreviewFileDialog = createAction(
  types.PREVIEW_FILE_DIALOG_CLOSE
);

export const openFAQModal = createAction(types.FAQ_MODAL_OPEN);
export const closeFAQModal = createAction(types.FAQ_MODAL_CLOSE);
export const openCastDistChartModal = createAction(
  types.CAST_DIST_CHART_MODAL_OPEN
);
export const closeCastDistChartModal = createAction(
  types.CAST_DIST_CHART_MODAL_CLOSE
);
export const castDistChartDataFetchRequest = createAction(
  types.CAST_DIST_CHART_DATA_FETCH_REQUEST
);
export const castDistChartDataFetchSuccess = createAction(
  types.CAST_DIST_CHART_DATA_FETCH_SUCCESS
);
export const castDistChartDataFetchError = createAction(
  types.CAST_DIST_CHART_DATA_FETCH_ERROR
);
export const setBrushExtent = createAction(types.SET_BRUSH_EXTENT);
export const clearBrush = createAction(types.CLEAR_BRUSH);
export const brushDidClear = createAction(types.BRUSH_DID_CLEAR);
export const setPreviewFileTitle = createAction(types.PREVIEW_FILE_SET_TITLE);
export const setPreviewFileBody = createAction(types.PREVIEW_FILE_SET_BODY);
export const downloadCastsInvalidate = createAction(
  types.DOWNLOAD_CASTS_INVALIDATE
);
export const downloadCastsFetchRequest = createAction(
  types.DOWNLOAD_CASTS_FETCH_REQUEST
);
export const downloadCastsFetchError = createAction(
  types.DOWNLOAD_CASTS_FETCH_ERROR
);
export const downloadCastsFetchSuccess = createAction(
  types.DOWNLOAD_CASTS_FETCH_SUCCESS,
  x => x,
  () => ({ receivedAt: Date.now() })
);
export const downloadDeviceInfoRequest = createAction(
  types.DOWNLOAD_DEVICE_INFO_REQUEST
);
export const downloadDeviceInfoSuccess = createAction(
  types.DOWNLOAD_DEVICE_INFO_SUCCESS
);
export const downloadDeviceInfoError = createAction(
  types.DOWNLOAD_DEVICE_INFO_ERROR
);
export const setTotalCastsWithFilters = createAction(
  types.SET_TOTAL_CASTS_WITH_FILTERS
);
export const setTotalCastsWithFiltersError = createAction(
  types.SET_TOTAL_CASTS_WITH_FILTERS_ERROR
);
export const downloadCastsChangeSelected = createAction(
  types.DOWNLOAD_CASTS_CHANGE_SELECTED
);
export const downloadFileFetchContentRequest = createAction(
  types.DOWNLOAD_FILE_FETCH_CONTENT_REQUEST
);
export const downloadFileFetchContentSuccess = createAction(
  types.DOWNLOAD_FILE_FETCH_CONTENT_SUCCESS
);
export const downloadFileFetchContentError = createAction(
  types.DOWNLOAD_FILE_FETCH_CONTENT_ERROR,
  x => x,
  (x, m) => m
);
export const downloadProcessPDFFileFetchContentRequest = createAction(
  types.DOWNLOAD_PROCESS_PDF_FILE_FETCH_CONTENT_REQUEST
);
export const downloadProcessPDFFileFetchContentSuccess = createAction(
  types.DOWNLOAD_PROCESS_PDF_FILE_FETCH_CONTENT_SUCCESS
);
export const setDownloadType = createAction(types.SET_CTD_DOWNLOAD_TYPE);
export const setProcessingStageFilter = createAction(
  types.SET_PROCESSING_STAGE_FILTER
);
export const setInstrumentFilter = createAction(types.SET_INSTRUMENT_FILTER);
export const setInstrumentFilterOverride = createAction(
  types.SET_INSTRUMENT_FILTER_OVERRIDE
);
export const setSerialNumFilter = createAction(types.SET_SERIAL_NUM_FILTER);
export const setWorkareaFilter = createAction(types.SET_WORKAREA_FILTER);
export const setOrganizationFilter = createAction(
  types.SET_ORGANIZATION_FILTER
);
export const setBufferFilter = createAction(types.SET_BUFFER_FILTER);
export const setUpcastFilter = createAction(types.SET_UPCAST_FILTER);
export const setFilenameFilter = createAction(types.SET_FILENAME_FILTER);
export const setCastPkFilter = createAction(types.SET_CAST_PK_FILTER);
export const setHakaiIdFilter = createAction(types.SET_HAKAI_ID_FILTER);
export const setSurveyFilter = createAction(types.SET_SURVEY_FILTER);
export const setStationFilter = createAction(types.SET_STATION_FILTER);
export const setSurveyStationOptions = createAction(
  types.SET_SURVEY_STATION_OPTIONS
);
export const updateSurveyStationOptionsFetchRequest = createAction(
  types.SET_SURVEY_STATION_OPTIONS_FETCH
);
export const setStartDateFilter = createAction(types.SET_START_DATE_FILTER);
export const setEndDateFilter = createAction(types.SET_END_DATE_FILTER);
export const setItemsPerPage = createAction(
  types.DOWNLOAD_CASTS_SET_ITEMS_PER_PAGE
);
export const setPageNumber = createAction(types.DOWNLOAD_CASTS_SET_PAGE_NUMBER);
export const zipDownloadFetchRequest = createAction(
  types.ZIP_DOWNLOAD_FETCH_REQUEST
);
export const zipDownloadFetchSuccess = createAction(
  types.ZIP_DOWNLOAD_FETCH_SUCCESS
);
export const zipDownloadFetchError = createAction(
  types.ZIP_DOWNLOAD_FETCH_ERROR
);
export const xlsxDownloadFetchRequest = createAction(
  types.XLSX_DOWNLOAD_FETCH_REQUEST
);
export const xlsxDownloadFetchSuccess = createAction(
  types.XLSX_DOWNLOAD_FETCH_SUCCESS
);
export const xlsxDownloadFetchError = createAction(
  types.XLSX_DOWNLOAD_FETCH_ERROR
);
export const csvDownloadFetchRequest = createAction(
  types.CSV_DOWNLOAD_FETCH_REQUEST
);
export const csvDownloadFetchSuccess = createAction(
  types.CSV_DOWNLOAD_FETCH_SUCCESS
);
export const csvDownloadFetchError = createAction(
  types.CSV_DOWNLOAD_FETCH_ERROR
);
export const xlsxDownloadMetadataFetchRequest = createAction(
  types.XLSX_DOWNLOAD_METADATA_FETCH_REQUEST
);
export const xlsxDownloadMetadataFetchSuccess = createAction(
  types.XLSX_DOWNLOAD_METADATA_FETCH_SUCCESS
);
export const xlsxDownloadMetadataFetchError = createAction(
  types.XLSX_DOWNLOAD_METADATA_FETCH_ERROR
);
export const csvDownloadMetadataFetchRequest = createAction(
  types.CSV_DOWNLOAD_METADATA_FETCH_REQUEST
);
export const csvDownloadMetadataFetchSuccess = createAction(
  types.CSV_DOWNLOAD_METADATA_FETCH_SUCCESS
);
export const csvDownloadMetadataFetchError = createAction(
  types.CSV_DOWNLOAD_METADATA_FETCH_ERROR
);
export const bulkUpdateDownloadFilters = createAction(
  types.BULK_UPDATE_DOWNLOAD_FILTERS
);
/* eslint-enable max-len */

//
const getCastFetchFilterQueryString = state => {
  const {
    workareaFilter,
    organizationFilter,
    surveyFilter,
    stationFilter,
    processingStageFilter,
    instrumentFilter,
    instrumentFilterOverride,
    serialNumFilter,
    startDateFilter,
    endDateFilter,
    filenameFilter,
    castPkFilter,
    hakaiIdFilter
  } = downloadPropsSelector(state);

  const querys = [
    `processing_stage>=${getEligibilityStage(processingStageFilter)}`,
    "status=null"
  ];

  if (instrumentFilterOverride) {
    querys.push(`device_model={${instrumentFilterOverride}}`);
  } else if (instrumentFilter !== "any") {
    querys.push(`device_model={${instrumentFilter}}`);
  }
  if (serialNumFilter !== "any")
    querys.push(`device_sn&&{"${serialNumFilter}", "0${serialNumFilter}"}`);
  if (workareaFilter.length > 0)
    querys.push(`work_area&&{${workareaFilter.map(x => `"${x}"`).join()}}`);
  if (organizationFilter.length > 0)
    querys.push(
      `organization&&{${organizationFilter.map(x => `"${x}"`).join()}}`
    );
  if (surveyFilter.length > 0)
    querys.push(`cruise&&{${surveyFilter.map(x => `"${x}"`).join()}}`);
  if (stationFilter.length > 0)
    querys.push(`station&&{${stationFilter.map(x => `"${x}"`).join()}}`);
  if (filenameFilter.length > 0)
    querys.push(`filename&&{${filenameFilter.map(x => `"${x}"`).join()}}`);
  if (castPkFilter.length > 0)
    querys.push(
      `ctd_cast_pk={${castPkFilter.map(x => parseInt(x, 10)).join()}}`
    );
  if (hakaiIdFilter.length > 0)
    querys.push(`hakai_id&&{${hakaiIdFilter.map(x => `"${x}"`).join()}}`);

  const isADate = str => moment(str, "YYYY-MM-DD", true).isValid();
  if (startDateFilter) {
    if (isADate(startDateFilter)) {
      const parsedStartDate = moment(startDateFilter)
        .tz("America/Los_Angeles")
        .toISOString();
      querys.push(`start_dt>=${parsedStartDate}`);
    }
  }
  if (endDateFilter) {
    if (isADate(endDateFilter)) {
      const parsedEndDate = moment(endDateFilter)
        .add(1, "days")
        .tz("America/Los_Angeles")
        .toISOString();
      querys.push(`start_dt<${parsedEndDate}`);
    }
  }
  return querys.join("&");
};

//
const downloadCastsFetch = () => (dispatch, getState) => {
  dispatch(downloadCastsFetchRequest());
  const state = getState();
  const { castsOffset, itemsPerPage } = downloadPropsSelector(state);
  const filterQuery = getCastFetchFilterQueryString(state);
  const query = [
    `limit=${itemsPerPage}`,
    `offset=${castsOffset}`,
    "sort=-start_dt",
    "fields=ctd_file_pk,ctd_cast_pk,filename,processing_stage,start_dt,cruise,station,cast_number,device_model,is_wirewalker"
  ].join("&");

  apiGet(`/ctd/views/file/cast?${filterQuery}&${query}`)
    .then(res => res.json())
    .then(casts => dispatch(downloadCastsFetchSuccess({ casts })))
    .catch(err => dispatch(downloadCastsFetchError(err)));
};

export const downLoadDeviceInfoFetch = () => dispatch => {
  dispatch(downloadDeviceInfoRequest());
  apiGet(`/ctd/views/file/cast?limit=-1&fields=device_model,device_sn`)
    .then(res => res.json())
    .then(casts => {
      const deviceModels = [];
      const deviceSn = [];
      casts.forEach(cast => {
        if (!deviceModels.includes(cast.device_model))
          deviceModels.push(cast.device_model);
        if (!deviceSn.includes(cast.device_sn)) deviceSn.push(cast.device_sn);
      });
      return { deviceModels, deviceSn };
    })
    .then(deviceInfo => dispatch(downloadDeviceInfoSuccess({ deviceInfo })))
    .catch(err => dispatch(downloadDeviceInfoError(err)));
};

export const downloadCastsFetchIfNeeded = () => (dispatch, getState) => {
  const state = getState();
  const {
    casts,
    isFetching,
    didInvalidate,
    pageNumber,
    itemsPerPage
  } = downloadPropsSelector(state);
  if (
    (casts.length < pageNumber * itemsPerPage || didInvalidate) &&
    !isFetching
  ) {
    dispatch(downloadCastsFetch());
  }
};

//
export const fetchTotalCastsWithFilters = () => (dispatch, getState) => {
  const query = getCastFetchFilterQueryString(getState());

  apiGet(`/ctd/views/file/cast?${query}&count`)
    .then(res => res.json())
    .then(json => dispatch(setTotalCastsWithFilters(json)))
    .catch(err => dispatch(setTotalCastsWithFiltersError(err)));
};

export const castDistChartDataFetch = () => (dispatch, getState) => {
  const query = getCastFetchFilterQueryString(getState());
  dispatch(castDistChartDataFetchRequest());

  apiGet(
    `/ctd/views/file/cast?fields=start_dt,bottom_depth,end_dt,target_depth&limit=-1&${query}`
  )
    .then(res => res.json())
    .then(data =>
      data.map(d => ({
        time: parseTime(d.start_dt),
        depth: d.bottom_depth || d.target_depth
      }))
    )
    .then(data => dispatch(castDistChartDataFetchSuccess(data)))
    .catch(err => dispatch(castDistChartDataFetchError(err)));
};

//
function downloadCastShouldFetchContent(cast, typeWanted) {
  const {
    contentIsFetching,
    content,
    contentDidInvalidate,
    contentType
  } = cast;
  return (
    (!content || contentDidInvalidate || contentType !== typeWanted) &&
    !contentIsFetching
  );
}
function handleDownloadError(err, dispatch, ctdCastPk) {
  if (err.status === 404) {
    dispatch(
      downloadFileFetchContentError(new Error("The file could not be found"), {
        ctd_cast_pk: ctdCastPk
      })
    );
  } else {
    dispatch(downloadFileFetchContentError(err, { ctd_cast_pk: ctdCastPk }));
  }
}
const downloadRskHexContent = async (
  filename,
  deviceModel,
  ctdCastPk,
  contentType,
  dispatch
) => {
  let contentPromise;
  let ext;
  let response;
  let content;
  // Seabird raw files will be in 'raw' folder
  // RBR raw files can be hex or rsk so have to check both directories
  // use async await to use try catch, but still have to wrap in promise to be
  // able to access the response and fit into existing error handling pattern
  // hex files are text and rsk are binary, so use file extension to determine how to handle the response

  return new Promise(async (resolve, reject) => {
    try {
      if (SEABIRD_MODELS.includes(deviceModel)) {
        contentPromise = await apiGet(`/ctd/s3/raw/${filename}`);
        ext = ".hex";
      } else {
        try {
          contentPromise = await apiGet(`/ctd/s3/rbrhex/${filename}`);
          ext = ".hex";
        } catch (err) {
          contentPromise = await apiGet(`/ctd/s3/rsk/${filename}`);
          ext = ".rsk";
        }
      }
      if (ext === ".hex") {
        response = contentPromise.json();
      } else response = contentPromise.arrayBuffer();
      response.then(res => {
        if (ext === ".hex") {
          content = res.Body;
        } else content = res;
        dispatch(
          downloadFileFetchContentSuccess({
            ctd_cast_pk: ctdCastPk,
            content,
            contentType
          })
        );
        resolve([content, ext]);
      });
    } catch (err) {
      handleDownloadError(err, dispatch, ctdCastPk);
      reject(err);
    }
  });
};
function downloadFileFetchContent(
  contentType,
  cast,
  dispatch,
  bufferFilter = false,
  upcastFilter = false,
  processingStageFilter
) {
  // eslint-disable-next-line camelcase
  const { ctd_cast_pk, selected, name, isWirewalker } = cast;
  let contentPromise;
  let dataType;
  let includeUpcast = upcastFilter;
  if (isWirewalker) {
    // wire walker data are only upcasts
    includeUpcast = true;
  }
  if (DATA_PROCESS_LEVELS.includes(selected)) {
    dataType = "data";
  } else if (selected === "1_datCnv" && bufferFilter) {
    dataType = "raw_data_buffer";
  } else {
    dataType = "raw_data";
  }

  if (downloadCastShouldFetchContent(cast, contentType)) {
    // If content not cached, get it and store in state
    if (contentType === "pdf") {
      dispatch(downloadProcessPDFFileFetchContentRequest(ctd_cast_pk));
    } else {
      dispatch(downloadFileFetchContentRequest(ctd_cast_pk));
    }

    // raw files must check multiple s3 directories so don't fit case pattern
    // separate function for this case

    if (contentType === "hexrsk") {
      contentPromise = downloadRskHexContent(
        name,
        cast.data.device_model,
        ctd_cast_pk,
        contentType,
        dispatch
      );
    } else {
      switch (contentType) {
        case "file":
          contentPromise = apiGet(`/ctd/s3/${selected}/${name}`)
            .then(res => res.json())
            .then(json => json.Body);
          break;
        case "xlsx":
          contentPromise = apiGet(
            // eslint-disable-next-line camelcase
            `/ctd/cast/xlsx/${ctd_cast_pk}/${dataType}/${includeUpcast}/${processingStageFilter}`
          ).then(res => res.arrayBuffer());
          break;
        case "csv":
          contentPromise = apiGet(
            // eslint-disable-next-line camelcase
            `/ctd/cast/csv/${ctd_cast_pk}/${dataType}/${includeUpcast}/${processingStageFilter}`
          ).then(res => res.text());
          break;
        case "pdf":
          // eslint-disable-next-line camelcase
          contentPromise = apiGet(`/ctd/s3/pdf/${ctd_cast_pk}`).then(res =>
            res.arrayBuffer()
          );
          break;
        default:
      }

      contentPromise
        .then(content => {
          if (contentType === "pdf") {
            dispatch(
              downloadProcessPDFFileFetchContentSuccess({
                ctd_cast_pk,
                content,
                contentType
              })
            );
          } else {
            dispatch(
              downloadFileFetchContentSuccess({
                ctd_cast_pk,
                content,
                contentType
              })
            );
          }
        })
        .catch(err => {
          handleDownloadError(err, dispatch, ctd_cast_pk);
        });
    }
  } else {
    // If content already cached, return it
    contentPromise = Promise.resolve(cast.content);
  }
  return contentPromise;
}

//
export const viewCsv = cast => (dispatch, getState) => {
  const state = getState();
  const {
    bufferFilter,
    upcastFilter,
    processingStageFilter
  } = downloadPropsSelector(state);
  downloadFileFetchContent(
    "csv",
    cast,
    dispatch,
    bufferFilter,
    upcastFilter,
    processingStageFilter
  )
    .then(content => dispatch(setPreviewFileBody(content)))
    .then(() => dispatch(setPreviewFileTitle(cast.name)))
    .then(() => dispatch(openPreviewFileDialog()));
};
export const viewFile = cast => dispatch => {
  downloadFileFetchContent("file", cast, dispatch)
    .then(content => dispatch(setPreviewFileBody(content)))
    .then(() => dispatch(setPreviewFileTitle(cast.name)))
    .then(() => dispatch(openPreviewFileDialog()));
};

//
export const downloadCsv = cast => (dispatch, getState) => {
  const state = getState();
  const {
    bufferFilter,
    upcastFilter,
    processingStageFilter
  } = downloadPropsSelector(state);
  downloadFileFetchContent(
    "csv",
    cast,
    dispatch,
    bufferFilter,
    upcastFilter,
    processingStageFilter
  ).then(content => {
    const blob = new Blob([content], { type: "text/plain" });
    saveAs(blob, `${cast.selected}_${cast.ctd_cast_pk}.csv`);
  });
};

export const downloadXlsx = cast => (dispatch, getState) => {
  const state = getState();
  const {
    bufferFilter,
    upcastFilter,
    processingStageFilter
  } = downloadPropsSelector(state);
  downloadFileFetchContent(
    "xlsx",
    cast,
    dispatch,
    bufferFilter,
    upcastFilter,
    processingStageFilter
  ).then(content => {
    const blob = new Blob([content], { type: "arraybuffer" });
    saveAs(blob, `${cast.selected}_${cast.ctd_cast_pk}.xlsx`);
  });
};

export const downloadFile = cast => dispatch => {
  downloadFileFetchContent("file", cast, dispatch).then(content => {
    const blob = new Blob([content], { type: "text/plain" });
    let ext;
    // if rbr txt file, if sb cnv file
    if (/^\d{6}_\d{8}_\d{4}$/g.test(cast.name)) ext = ".txt";
    else ext = ".cnv";
    saveAs(blob, `${cast.selected}_${cast.name}${ext}`);
  });
};
// in this case only downloadfilefetchcontent will return an array with the file body and file extension
export const downloadHexRsk = cast => dispatch => {
  downloadFileFetchContent("hexrsk", cast, dispatch).then(content => {
    const ext = content[1];
    let blob;
    if (ext === ".hex") blob = new Blob([content[0]], { type: "text/plain" });
    else blob = new Blob([content[0]], { type: "arraybuffer" });
    saveAs(blob, `${cast.selected}_${cast.name}${ext}`);
  });
};

export const downloadPDF = cast => dispatch => {
  downloadFileFetchContent("pdf", cast, dispatch).then(content => {
    const blob = new Blob([content], { type: "arraybuffer" });
    saveAs(blob, `2_Processed_${cast.ctd_cast_pk}.pdf`);
  });
};

//
export const nextPage = () => (dispatch, getState) => {
  const { pageNumber } = downloadPropsSelector(getState());
  dispatch(setPageNumber(pageNumber + 1));
  dispatch(downloadCastsFetchIfNeeded());
};
export const prevPage = () => (dispatch, getState) => {
  const { pageNumber } = downloadPropsSelector(getState());
  if (pageNumber > 1) dispatch(setPageNumber(pageNumber - 1));
};

//
export const updateSurveyStationOptions = () => dispatch => {
  dispatch(updateSurveyStationOptionsFetchRequest());
  apiGet(
    "/ctd/views/file/cast?limit=-1&fields=cruise,station,work_area,organization&distinct"
  )
    .then(res => res.json())
    .then(json => dispatch(setSurveyStationOptions(json)))
    .catch(err => dispatch(setSurveyStationOptions(err)));
};

// Backs "Download All Data" for the "Original files" download types - i.e. when
// downloadType is "file" (file/1_datCnv, file/8_binAvg) or "hexrsk"
// (hexrsk/raw), dispatched from handleDownloadAllClick in DownloadView.
// The csv/xlsx download types go through csv/xlsxDownloadWithFilters instead.
// processingStageFilter is used raw as the s3 prefix, which is only valid for
// the stages s3 actually holds files for - the api whitelists up to 8_binAvg
// (see prefixTypes in hakai-api routes/ctd/s3.js). QC'd data lives in the db,
// not s3, so there is no file/ download type for 9_qc_auto and later.
export const zipDownloadWithFilters = () => (dispatch, getState) => {
  dispatch(zipDownloadFetchRequest());
  const state = getState();
  const { processingStageFilter } = downloadPropsSelector(state);
  const query = getCastFetchFilterQueryString(state);

  apiGet(
    `/ctd/views/file/cast?${query}&fields=cruise,station,work_area,organization,filename,start_dt&limit=-1`
  )
    .then(res => res.json())
    .then(json =>
      apiPost(`/ctd/s3/bulk/${processingStageFilter}`, json, {
        responseType: "arraybuffer"
      })
    )
    .then(res => res.blob())
    .then(blob => saveAs(blob, `ctd-bulk-${Date.now()}.zip`))
    .then(() => dispatch(zipDownloadFetchSuccess()))
    .catch(err => dispatch(zipDownloadFetchError(err)));
};

export const xlsxDownloadWithFilters = () => (dispatch, getState) => {
  dispatch(xlsxDownloadFetchRequest());
  const state = getState();
  const query = getCastFetchFilterQueryString(state);
  const { processingStageFilter, upcastFilter } = downloadPropsSelector(state);
  const dataType = DATA_PROCESS_LEVELS.includes(processingStageFilter)
    ? "data"
    : "raw_data";

  apiGet(`/ctd/views/file/cast?${query}&fields=ctd_cast_pk&limit=-1`)
    .then(res => res.json())
    .then(json =>
      apiPost(
        `/ctd/cast/bulk/${dataType}.xlsx/${upcastFilter}/${processingStageFilter}`,
        json,
        {
          responseType: "arraybuffer"
        }
      )
    )
    .then(res => res.blob())
    .then(blob => saveAs(blob, `${processingStageFilter}-${Date.now()}.xlsx`))
    .then(() => dispatch(xlsxDownloadFetchSuccess()))
    .catch(err => dispatch(xlsxDownloadFetchError(err)));
};

export const csvDownloadWithFilters = () => (dispatch, getState) => {
  dispatch(csvDownloadFetchRequest());
  const state = getState();
  const query = getCastFetchFilterQueryString(state);
  const { processingStageFilter, upcastFilter } = downloadPropsSelector(state);
  const dataType = DATA_PROCESS_LEVELS.includes(processingStageFilter)
    ? "data"
    : "raw_data";

  apiGet(`/ctd/views/file/cast?${query}&fields=ctd_cast_pk&limit=-1`)
    .then(res => res.json())
    .then(json =>
      apiPost(
        `/ctd/cast/bulk/${dataType}.csv/${upcastFilter}/${processingStageFilter}`,
        json,
        {
          responseType: "arraybuffer"
        }
      )
    )
    .then(res => res.blob())
    .then(blob => saveAs(blob, `${processingStageFilter}-${Date.now()}.csv`))
    .then(() => dispatch(csvDownloadFetchSuccess()))
    .catch(err => dispatch(csvDownloadFetchError(err)));
};

export const xlsxDownloadMetadataWithFilters = () => (dispatch, getState) => {
  dispatch(xlsxDownloadMetadataFetchRequest());
  const state = getState();
  const query = getCastFetchFilterQueryString(state);

  apiGet(`/ctd/views/file/cast?${query}&fields=ctd_cast_pk&limit=-1`)
    .then(res => res.json())
    .then(json =>
      apiPost(`/ctd/cast/meta/xlsx`, json, { responseType: "arraybuffer" })
    )
    .then(res => res.blob())
    .then(blob => saveAs(blob, `ctd-meta-${Date.now()}.xlsx`))
    .then(() => dispatch(xlsxDownloadMetadataFetchSuccess()))
    .catch(err => dispatch(xlsxDownloadMetadataFetchError(err)));
};

export const csvDownloadMetadataWithFilters = () => (dispatch, getState) => {
  dispatch(csvDownloadMetadataFetchRequest());
  const state = getState();
  const query = getCastFetchFilterQueryString(state);

  apiGet(`/ctd/views/file/cast?${query}&fields=ctd_cast_pk&limit=-1`)
    .then(res => res.json())
    .then(json =>
      apiPost(`/ctd/cast/meta/csv`, json, { responseType: "arraybuffer" })
    )
    .then(res => res.blob())
    .then(blob => saveAs(blob, `ctd-meta-${Date.now()}.csv`))
    .then(() => dispatch(csvDownloadMetadataFetchSuccess()))
    .catch(err => dispatch(csvDownloadMetadataFetchError(err)));
};

export const createUrlPayload = queryString => {
  const urlParams = new URLSearchParams(queryString);
  const payloadObj = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of urlParams.entries()) {
    if (key.includes("Date")) {
      payloadObj[key] = Immutable.fromJS(moment(value));
    } else {
      payloadObj[key] = Immutable.fromJS(value.split(","));
    }
  }
  return payloadObj;
};

export const updateFiltersFromQuery = queryString => dispatch => {
  const payloadObj = createUrlPayload(queryString);
  const action = bulkUpdateDownloadFilters(payloadObj);
  dispatch(action);
};
